import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchMe = useCallback(async () => {
    const token = localStorage.getItem("folio_token");
    if (!token) {
      setUser(null);
      setLoading(false);
      return;
    }
    try {
      const res = await api.get("/auth/me");
      setUser(res.data);
    } catch {
      localStorage.removeItem("folio_token");
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMe();
  }, [fetchMe]);

  const login = async (email, password) => {
    const res = await api.post("/auth/login", { email, password });
    localStorage.setItem("folio_token", res.data.token);
    setUser(res.data.user);
    return res.data.user;
  };

  const googleComplete = async (sessionId) => {
    // Exchange the Emergent OAuth session_id for our JWT.
    const res = await api.post("/auth/google/complete", { session_id: sessionId });
    localStorage.setItem("folio_token", res.data.token);
    setUser(res.data.user);
    return res.data.user;
  };

  const signup = async (email, password, name, turnstileToken) => {
    const res = await api.post("/auth/signup", { email, password, name, turnstile_token: turnstileToken });
    // Backend now requires email verification before login is possible.
    return res.data; // { verification_required: true, email }
  };

  const forgotPassword = async (email) => {
    await api.post("/auth/forgot-password", { email });
  };

  const resetPassword = async (token, password) => {
    await api.post("/auth/reset-password", { token, password });
  };

  const verifyEmail = async (token) => {
    const res = await api.post("/auth/verify-email", { token });
    localStorage.setItem("folio_token", res.data.token);
    setUser(res.data.user);
    return res.data.user;
  };

  const resendVerification = async (email) => {
    await api.post("/auth/resend-verification", { email });
  };

  const logout = () => {
    localStorage.removeItem("folio_token");
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, signup, googleComplete, logout, refresh: fetchMe, forgotPassword, resetPassword, verifyEmail, resendVerification }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
